/**
 * E024-S001-T001: CsvExtractor
 *
 * Parses CSV files and detects column types automatically.
 * Uses Papa Parse for CSV parsing and shared type detection utilities.
 */

import { nanoid } from 'nanoid';
import Papa from 'papaparse';

import type { ColumnType } from './types';
import { detectColumnType, parseValue } from './utils';

export interface CsvExtractorOptions {
  file: File;
  hasHeader?: boolean; // default: true
  delimiter?: string; // auto-detect if not provided
  encoding?: string; // auto-detect if not provided
}

export interface CsvExtractionResult {
  name: string; // Filename ohne Extension
  columns: CsvColumnDef[];
  rows: Record<string, unknown>[];
  rowCount: number;
}

export interface CsvColumnDef {
  id: string; // Generierte UUID
  name: string; // Header oder "Column A", "Column B"
  type: ColumnType; // Erkannter Typ
}

export class CsvExtractor {
  async extract(options: CsvExtractorOptions): Promise<CsvExtractionResult> {
    const { file, hasHeader = true, delimiter } = options;

    // 1. Parse CSV mit Papa Parse
    const text = await file.text();
    const parsed = Papa.parse(text, {
      header: false,
      skipEmptyLines: true,
      dynamicTyping: false, // Wir machen eigene Type Detection
      delimiter: delimiter, // Auto-detect if undefined
    });

    const rawRows = parsed.data as string[][];
    if (rawRows.length === 0) {
      throw new Error('CSV file is empty');
    }

    // 2. Header extrahieren
    const headerRow = hasHeader ? rawRows[0] : null;
    const dataRows = hasHeader ? rawRows.slice(1) : rawRows;

    // 3. Spalten definieren
    const columnCount = Math.max(...rawRows.map(r => r.length));
    const columns = this.createColumns(headerRow, columnCount, dataRows);

    // 4. Rows konvertieren
    const rows = this.convertRows(dataRows, columns);

    return {
      name: file.name.replace(/\.csv$/i, ''),
      columns,
      rows,
      rowCount: rows.length,
    };
  }

  private createColumns(
    headerRow: string[] | null,
    columnCount: number,
    dataRows: string[][]
  ): CsvColumnDef[] {
    const columns: CsvColumnDef[] = [];

    for (let i = 0; i < columnCount; i++) {
      const name =
        headerRow?.[i]?.trim() || `Column ${String.fromCharCode(65 + i)}`;
      const values = dataRows.map(row => row[i] || '');

      // Use shared type detection
      const type = detectColumnType(values);

      columns.push({
        id: nanoid(),
        name,
        type,
      });
    }

    return columns;
  }

  private convertRows(
    dataRows: string[][],
    columns: CsvColumnDef[]
  ): Record<string, unknown>[] {
    return dataRows.map(row => {
      const record: Record<string, unknown> = {};
      columns.forEach((col, i) => {
        const value = row[i] || '';
        // Use shared parseValue utility
        record[col.id] = parseValue(value, col.type);
      });
      return record;
    });
  }
}
